import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAtom, useAtomValue } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, Stage } from 'react-konva';
import Modal from 'react-modal';
import BoardTransformer from '@/components/Board/BoardTransformer';
import ElementDescriptionPopup from '@/components/Board/ElementDescriptionPopup';
import GridLayer from '@/components/Board/GridLayer';
import ImageCropEditor from '@/components/Board/ImageCropEditor';
import MaskTransformer from '@/components/Board/MaskTransformer';
import { RulerTool } from '@/components/Board/RulerTool';
import SelectionRect from '@/components/Board/SelectionRect';
import { InlineTextEditor } from '@/components/Board/TextEditor';
import MultiUnifiedCloudinaryForm from '@/components/MultiUnifiedCloudinaryForm';
import NotificationPopup from '@/components/UI/NotificationPopup';
import UnifiedCloudinaryForm from '@/components/UnifiedCloudinaryForm';
import { useGlobalMouseUpForSelection, useOptimizedRendering, useStageCursor, useStageDragAndDrop, useStageDragEvents, useStageDraggableSync, useStageHotkeys, useStageMouseEvents, useStageTouchEvents, useStageTransformEvents, useStageViewport, useStageVisibility, useStageWheelEvents, } from '@/hooks/board';
import { useBoardHistory } from '@/hooks/useHistory';
import { useNotification } from '@/hooks/useNotification';
import { boardStateAtom, descriptionPopupElementIdAtom, draftSelectionIdsAtom, gridSizeAtom, imageCropEditorAtom, isPanningAtom, isSelectingAtom, isZoomingAtom, maskEditorAtom, movingElementsIdsAtom, roleAtom, rulerStateAtom, selectedIdsAtom, selectionRectAtom, showGridAtom, snapToGridAtom, stageModeAtom, stageRefAtom, stageViewportAtom, } from '@/store/boardAtoms';
import { setPanZoomActive } from '@/utils/board/imageCacheUtils';
import { createElementDragBoundFunc, getInsertPosition } from '@/utils/board/insertionUtils';
import { renderLayers } from '@/utils/board/layerRenderingUtils';
import { draftStateAfterUpdate, getSnappedXY } from '@/utils/board/stageUtils';
/**
 * BoardStage — основной компонент для рендеринга интерактивной доски.
 *
 * Компонент отвечает за:
 * - Рендеринг всех элементов доски (изображения, текст, фигуры)
 * - Обработку взаимодействий пользователя (мышь, клавиатура, touch)
 * - Управление состоянием выделения и трансформации элементов
 * - Виртуализацию элементов для оптимизации производительности
 * - Синхронизацию изменений с сервером через WebSocket
 *
 * @param send - Функция для отправки сообщений на сервер
 */
const BoardStage = ({ send }) => {
    var _a;
    const { t } = useTranslation();
    // ==================== СОСТОЯНИЕ ИЗ АТОМОВ ====================
    const [role] = useAtom(roleAtom);
    const { boardState, setBoardState } = useBoardHistory();
    const { setSize, setPosition } = useStageViewport();
    // Используем useAtomValue для чтения viewport
    // ВАЖНО: viewport используется только для рендеринга Stage (x, y, scaleX, scaleY)
    // Stage обновляется через React props, но это необходимо для синхронизации с Konva
    // Другие компоненты не должны ререндериться при изменении viewport
    const viewport = useAtomValue(stageViewportAtom);
    const { zoom, position: stagePosition, size: stageSize } = viewport;
    const [showGrid] = useAtom(showGridAtom);
    const [snapToGrid] = useAtom(snapToGridAtom);
    const [gridSize] = useAtom(gridSizeAtom);
    const [stageMode] = useAtom(stageModeAtom);
    const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);
    const [isSelecting] = useAtom(isSelectingAtom);
    const [draftSelectionIds] = useAtom(draftSelectionIdsAtom);
    const [selectionRect] = useAtom(selectionRectAtom);
    const [, setStageRef] = useAtom(stageRefAtom);
    const [isPanning] = useAtom(isPanningAtom);
    const [isZooming] = useAtom(isZoomingAtom);
    const [maskEditor] = useAtom(maskEditorAtom);
    const [imageCropEditor, setImageCropEditor] = useAtom(imageCropEditorAtom);
    const [movingElementsIds] = useAtom(movingElementsIdsAtom);
    const [rulerState, setRulerState] = useAtom(rulerStateAtom);
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // ==================== ЛОКАЛЬНОЕ СОСТОЯНИЕ ====================
    const [editingTextElement, setEditingTextElement] = useState(null);
    const [descriptionPopupElementId, setDescriptionPopupElementId] = useAtom(descriptionPopupElementIdAtom);
    const [noteDragPosition, setNoteDragPosition] = useState(null);
    // ==================== РЕФЫ ====================
    const stageRef = useRef(null);
    const transformerRef = useRef(null);
    const pointerPositionRef = useRef(null);
    const wasStageDraggedRef = useRef(false);
    const elementClickId = useRef(null);
    const renderedLayersRef = useRef(null);
    const renderedElementsMapRef = useRef(new Map());
    const elementHashesRef = useRef(new Map());
    const previousSelectedIdsRef = useRef(new Set());
    const previousDraftSelectionRef = useRef(new Set());
    // Сбрасываем состояние линейки при переключении режима
    useEffect(() => {
        if (stageMode !== 'ruler' && rulerState.isActive) {
            setRulerState({
                isActive: false,
                startPoint: null,
                endPoint: null,
                distance: null,
                unit: 'px',
            });
        }
    }, [stageMode, rulerState.isActive, setRulerState]);
    const { notificationList, showNotification, timeout } = useNotification(3500);
    // ==================== ВЫЧИСЛЯЕМЫЕ ЗНАЧЕНИЯ ====================
    const canEdit = role !== 'VIEWER';
    const visibleElements = useMemo(() => {
        const currentLayers = boardState.layers || [];
        const result = [];
        for (const layer of currentLayers) {
            if (layer.visible) {
                for (const element of layer.elements || []) {
                    result.push(element);
                }
            }
        }
        return result;
    }, [boardState.layers]);
    // Создаем Map для быстрого поиска элементов по ID
    const visibleElementsMap = useMemo(() => {
        const map = new Map();
        for (const element of visibleElements) {
            map.set(element.id, element);
        }
        return map;
    }, [visibleElements]);
    const selectedIdsSet = useMemo(() => new Set(selectedIds), [selectedIds]);
    const selectedElements = useMemo(() => {
        if (selectedIds.length === 0)
            return [];
        if (selectedIds.length === 1) {
            const id = selectedIds[0];
            const element = visibleElementsMap.get(id);
            return element ? [element] : [];
        }
        return selectedIds
            .map((id) => visibleElementsMap.get(id))
            .filter((el) => el !== undefined);
    }, [visibleElementsMap, selectedIds]);
    // Получаем актуальный элемент из boardState для попапа описания
    const descriptionPopupElement = useMemo(() => {
        if (!descriptionPopupElementId)
            return null;
        return visibleElementsMap.get(descriptionPopupElementId) || null;
    }, [descriptionPopupElementId, visibleElementsMap]);
    // ==================== ОБРАБОТЧИКИ ====================
    const getInsertPositionCallback = useCallback(() => {
        const currentViewport = store.get(stageViewportAtom);
        return getInsertPosition(pointerPositionRef, () => store.get(stageRefAtom), currentViewport.zoom);
    }, [store]);
    const notifyActionBlocked = useCallback((action) => {
        if (action === 'delete') {
            showNotification(t('board.notifications.deleteBlocked', 'Удаление недоступно: элемент заблокирован или является презентационным фреймом.'));
            return;
        }
        showNotification(t('board.notifications.moveBlocked', 'Перемещение недоступно: элемент заблокирован.'));
    }, [showNotification, t]);
    const elementDragBoundFunc = useMemo(() => createElementDragBoundFunc(() => store.get(stageRefAtom), // Используем функцию для получения актуального Stage
    gridSize, () => store.get(snapToGridAtom), // Используем функцию для получения актуального значения атома
    () => store.get(boardStateAtom), // Используем функцию для получения актуального boardState
    getSnappedXY), [gridSize, store]);
    // Синхронизируем draggable состояние элементов с режимом Stage
    useStageDraggableSync();
    const handleElementDoubleClick = useCallback((e, elementId) => {
        if (!canEdit)
            return;
        const element = visibleElementsMap.get(elementId);
        if (!element || element.locked)
            return;
        if (element.elementType === 'text') {
            setSelectedIds([]);
            setEditingTextElement(element);
        }
        else if (element.elementType === 'image' || element.elementType === 'token') {
            setImageCropEditor({ active: true, element });
        }
    }, [canEdit, visibleElementsMap, setImageCropEditor, setSelectedIds]);
    const handleTextEditCancel = useCallback(() => {
        setEditingTextElement(null);
    }, []);
    const handleTextSave = useCallback((elementId, newText, styles) => {
        const payload = [
            {
                id: elementId,
                props: Object.assign({ text: newText }, styles),
            },
        ];
        setBoardState(draftStateAfterUpdate(boardState, payload));
        send({ type: 'element.update', payload });
        setEditingTextElement(null);
    }, [boardState, setBoardState, send]);
    const handleDescriptionIconClick = useCallback((e, elementId) => {
        e.cancelBubble = true;
        setDescriptionPopupElementId(elementId);
    }, [setDescriptionPopupElementId]);
    const handleDescriptionEdit = useCallback((elementId, description) => {
        const payload = [
            {
                id: elementId,
                description,
            },
        ];
        setBoardState((prevState) => draftStateAfterUpdate(prevState, payload));
        send({ type: 'element.update', payload });
    }, [setBoardState, send]);
    const handleDescriptionPopupClose = useCallback(() => {
        setDescriptionPopupElementId(null);
    }, [setDescriptionPopupElementId]);
    // ==================== ХУКИ ДЛЯ ОБРАБОТКИ СОБЫТИЙ ====================
    const mouseEvents = useStageMouseEvents({
        boardState,
        canEdit,
        visibleElements,
        pointerPositionRef,
        wasStageDraggedRef,
        elementClickId,
    });
    const dragEvents = useStageDragEvents({
        boardState,
        setBoardState,
        canEdit,
        visibleElements,
        send,
        getElementClickInfo: mouseEvents.getElementClickInfo,
        setPosition,
    });
    const wheelEvents = useStageWheelEvents({
        selectedIds,
    });
    const touchEvents = useStageTouchEvents({
        selectedIds,
    });
    const transformEvents = useStageTransformEvents({
        boardState,
        setBoardState,
        canEdit,
        selectedElements,
        send,
    });
    const { uploadModalOpen, onUploadModalClose, uploadDefaultFile, onFormUploaded, multiUploadModalOpen, onMultiUploadModalClose, uploadDefaultFiles, onMultiFormUploaded, isControlPressed, onPasteImage, onPasteImages, onPasteText, } = useStageHotkeys({
        boardState,
        setBoardState,
        selectedIds,
        visibleElements,
        canEdit,
        send,
        getInsertPosition: getInsertPositionCallback,
        gridSize,
        snapToGrid,
        zoomIn: wheelEvents.zoomIn,
        zoomOut: wheelEvents.zoomOut,
        notifyActionBlocked,
    });
    useGlobalMouseUpForSelection();
    useStageDragAndDrop({
        pointerPositionRef,
        canEdit,
        boardState,
        send,
        onPasteImage,
        onPasteImages,
        onPasteText,
        onNoteDragPositionChange: setNoteDragPosition,
    });
    // ==================== ЭФФЕКТЫ ====================
    useEffect(() => {
        setStageRef(stageRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useStageCursor();
    useEffect(() => {
        const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setSize]);
    // Обновление Transformer при изменении выделения
    useEffect(() => {
        const transformer = transformerRef.current;
        const stage = store.get(stageRefAtom);
        if (!transformer || !stage)
            return;
        if (isPanning || isZooming) {
            // Очищаем Transformer во время pan/zoom для оптимизации
            transformer.nodes([]);
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (selectedElements.length === 0) {
            // Очищаем Transformer если выделение было снято
            transformer.nodes([]);
        }
        else {
            const nodes = selectedElements
                .filter((el) => !el.locked)
                .map((el) => stage.findOne(`#node-${el.id}`))
                .filter((node) => node !== undefined && node.isVisible());
            transformer.nodes(nodes);
        }
        return () => {
            if (transformer) {
                transformer.nodes([]);
            }
        };
    }, [
        selectedElements,
        selectedIds,
        draftSelectionIds,
        isPanning,
        isZooming,
        store,
        transformEvents.isTransformingRef,
    ]);
    // Автопан при выходе курсора за пределы окна
    useEffect(() => {
        const onMouseMove = (e) => {
            const isDraggingElement = dragEvents.elementMoveId.current !== null;
            if (!isSelecting && !isDraggingElement)
                return;
            const { innerWidth, innerHeight } = window;
            let dx = 0;
            let dy = 0;
            if (e.clientX <= 0)
                dx = 20;
            else if (e.clientX >= innerWidth - 1)
                dx = -20;
            if (e.clientY <= 0)
                dy = 20;
            else if (e.clientY >= innerHeight - 1)
                dy = -20;
            if (dx !== 0 || dy !== 0) {
                setPosition((pos) => ({ x: pos.x + dx, y: pos.y + dy }));
            }
        };
        window.addEventListener('mousemove', onMouseMove);
        return () => window.removeEventListener('mousemove', onMouseMove);
        // dragEvents.elementMoveId - это ref, не должен быть в зависимостях
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSelecting, setPosition]);
    // Виртуализация: обновление видимости элементов при изменении viewport
    useStageVisibility({
        boardState,
        isPanning,
        isZooming,
    });
    // Оптимизация кеширования: ограничиваем параллелизм во время pan/zoom
    useEffect(() => {
        setPanZoomActive(isPanning || isZooming);
    }, [isPanning, isZooming]);
    // Очистка при размонтировании
    useEffect(() => {
        const elementHashes = elementHashesRef.current;
        const previousSelectedIds = previousSelectedIdsRef.current;
        const previousDraftSelection = previousDraftSelectionRef.current;
        const renderedElementsMap = renderedElementsMapRef.current;
        return () => {
            // Очищаем Stage
            const stage = store.get(stageRefAtom);
            if (stage) {
                try {
                    stage.destroy();
                }
                catch (e) {
                    // Игнорируем ошибки при уничтожении
                }
                setStageRef(null);
            }
            // Очищаем кэш рендеров
            renderedLayersRef.current = null;
            renderedElementsMap.clear();
            elementHashes.clear();
            previousSelectedIds.clear();
            previousDraftSelection.clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ==================== РЕНДЕРИНГ ====================
    const keepRatio = useMemo(() => {
        var _a;
        if (selectedElements.length > 1) {
            return true;
        }
        if (selectedElements.length === 1) {
            if (isControlPressed) {
                return false;
            }
            const elType = (_a = selectedElements[0]) === null || _a === void 0 ? void 0 : _a.elementType;
            if (elType === 'image' || elType === 'token') {
                return true;
            }
        }
        return false;
    }, [selectedElements, isControlPressed]);
    const stateHash = useMemo(() => { var _a; return `${((_a = boardState.layers) === null || _a === void 0 ? void 0 : _a.length) || 0}-${selectedIds.join(',')}-${draftSelectionIds.join(',')}-${canEdit}-${stageMode}-${(editingTextElement === null || editingTextElement === void 0 ? void 0 : editingTextElement.id) || ''}`; }, [
        (_a = boardState.layers) === null || _a === void 0 ? void 0 : _a.length,
        selectedIds,
        draftSelectionIds,
        canEdit,
        stageMode,
        editingTextElement === null || editingTextElement === void 0 ? void 0 : editingTextElement.id,
    ]);
    const renderLayersCallback = useCallback(() => {
        return renderLayers({
            boardState,
            selectedIds,
            selectedIdsSet,
            draftSelectionIds,
            canEdit,
            stageMode,
            elementDragBoundFunc,
            handleElementDoubleClick,
            handleDescriptionIconClick,
            editingTextElement,
            viewport,
            elementHashesRef,
            previousSelectedIdsRef,
            previousDraftSelectionRef,
            renderedLayersRef,
            renderedElementsMapRef,
            isPanning,
            isZooming,
            isTransforming: transformEvents.isTransformingRef.current,
            isCropping: imageCropEditor.active,
            movingElementsIds,
        });
        // Исключаем zoom, stagePosition, stageSize, isPanning, isZooming из зависимостей
        // чтобы предотвратить перерендеринг во время панорамирования/зума
        // Эти значения используются только для вычисления видимости или предотвращения очистки кэша,
        // что не критично во время активного взаимодействия
        // isPanning/isZooming передаются в renderLayers, но их изменения не должны вызывать перерендеринг
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        boardState.layers,
        selectedIds,
        selectedIdsSet,
        draftSelectionIds,
        canEdit,
        stageMode,
        elementDragBoundFunc,
        handleElementDoubleClick,
        handleDescriptionIconClick,
        editingTextElement,
        transformEvents.isTransformingRef,
        imageCropEditor.active,
        movingElementsIds,
        // zoom, stagePosition, stageSize, isPanning, isZooming - исключены для оптимизации
    ]);
    const renderedLayers = useOptimizedRendering({
        isPanning,
        isZooming,
        stateHash,
        renderFn: renderLayersCallback,
        deps: [
            boardState.layers,
            selectedIds,
            draftSelectionIds,
            canEdit,
            stageMode,
            elementDragBoundFunc,
            handleElementDoubleClick,
            editingTextElement,
            movingElementsIds,
            // zoom, stagePosition, stageSize - исключены для оптимизации
            // чтобы предотвратить перерендеринг во время панорамирования/зума
        ],
    });
    // ==================== РЕНДЕР ====================
    return (_jsxs(_Fragment, { children: [noteDragPosition && (_jsx("div", { style: {
                    position: 'absolute',
                    left: `${noteDragPosition.x * zoom + stagePosition.x}px`,
                    top: `${noteDragPosition.y * zoom + stagePosition.y}px`,
                    width: `${noteDragPosition.width * zoom}px`,
                    height: `${noteDragPosition.height * zoom}px`,
                    border: '2px dashed rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4,
                    pointerEvents: 'none',
                    zIndex: 10000,
                    padding: `${12 * zoom}px`,
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }, children: _jsx("div", { style: {
                        width: '100%',
                        height: '100%',
                        fontSize: `${18 * zoom}px`,
                        fontFamily: 'Nunito, sans-serif',
                        color: 'rgba(255, 255, 255, 0.7)',
                        lineHeight: 1.4,
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflow: 'hidden',
                        textAlign: 'left',
                    }, children: noteDragPosition.text || '' }) })), _jsxs(Stage, { ref: stageRef, x: stagePosition.x, y: stagePosition.y, width: stageSize.width, height: stageSize.height, scaleX: zoom, scaleY: zoom, onMouseDown: mouseEvents.onStageMouseDown, onMouseMove: mouseEvents.onStageMouseMove, onMouseUp: mouseEvents.onStageMouseUp, onMouseLeave: mouseEvents.onStageMouseLeave, onDblClick: mouseEvents.onStageDoubleClick, onWheel: touchEvents.onWheel, onTouchStart: touchEvents.onTouchStart, onTouchMove: touchEvents.onTouchMove, onTouchEnd: touchEvents.onTouchEnd, draggable: (stageMode === 'move' || isPanning) && !isZooming, onDragStart: dragEvents.onStageDragStart, onDragMove: dragEvents.onStageDragMove, onDragEnd: dragEvents.onStageDragEnd, onContextMenu: mouseEvents.handleContextMenu, children: [showGrid && store.get(stageRefAtom) && (_jsx(GridLayer, { showGrid: showGrid, gridSize: gridSize, zoom: zoom, stageSize: stageSize })), _jsxs(Layer, { perfectDrawEnabled: false, imageSmoothingEnabled: true, hitGraphEnabled: true, shadowForStrokeEnabled: false, listening: !isPanning && !isZooming, children: [renderedLayers, stageMode === 'select' &&
                                (role === 'EDITOR' || role === 'GM') &&
                                !maskEditor.active &&
                                !imageCropEditor.active && (_jsx(BoardTransformer, { ref: transformerRef, zoom: zoom, keepRatio: keepRatio, gridSize: gridSize, snapToGrid: snapToGrid, selectedElements: selectedElements, onTransformEnd: transformEvents.onTransformEnd, onDragStart: transformEvents.transformerOnDragStart, onDragEnd: transformEvents.transformerOnDragEnd })), _jsx(MaskTransformer, { boardState: boardState, setBoardState: setBoardState, send: send }), _jsx(ImageCropEditor, { boardState: boardState, setBoardState: setBoardState, send: send }), selectionRect && _jsx(SelectionRect, { selectionRect: selectionRect }), rulerState.isActive && (_jsx(RulerTool, { startPoint: rulerState.startPoint, endPoint: rulerState.endPoint, distance: rulerState.distance, unit: rulerState.unit, zoom: zoom }))] })] }), editingTextElement && (_jsx(InlineTextEditor, { element: editingTextElement, onSave: handleTextSave, onCancel: handleTextEditCancel, stagePosition: stagePosition, zoom: zoom })), _jsx(Modal, { isOpen: uploadModalOpen, onRequestClose: onUploadModalClose, contentLabel: "Upload image", style: {
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                    },
                    content: {
                        position: 'relative',
                        background: '#fff',
                        borderRadius: 12,
                        padding: 12,
                        maxWidth: 700,
                        width: '96%',
                        maxHeight: '90vh',
                        inset: 'auto',
                        border: 'none',
                        overflow: 'visible',
                    },
                }, children: _jsx(UnifiedCloudinaryForm, { defaultFile: uploadDefaultFile, onUploaded: onFormUploaded, maxFileMB: 10, uploadPreset: 'images' }) }), _jsx(Modal, { isOpen: multiUploadModalOpen, onRequestClose: onMultiUploadModalClose, contentLabel: "Upload images", style: {
                    overlay: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                    },
                    content: {
                        position: 'relative',
                        background: '#fff',
                        borderRadius: 12,
                        padding: 0,
                        maxWidth: 900,
                        width: '96%',
                        maxHeight: '90vh',
                        inset: 'auto',
                        border: 'none',
                        overflow: 'visible',
                    },
                }, children: _jsx(MultiUnifiedCloudinaryForm, { defaultFiles: uploadDefaultFiles, onUploaded: onMultiFormUploaded, maxFileMB: 10, uploadPreset: 'images' }) }), _jsx(NotificationPopup, { notificationList: notificationList, timeout: timeout }), _jsx(ElementDescriptionPopup, { isOpen: !!descriptionPopupElement, element: descriptionPopupElement, stagePosition: stagePosition, zoom: zoom, onClose: handleDescriptionPopupClose, onEdit: handleDescriptionEdit })] }));
};
export default BoardStage;
